import React, { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid2,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Button,
  Chip
} from '@mui/material';
import { getFullTimeTable, generateTimetable } from '../../services/TimeScheduler.service';
import useAuth from 'hooks/useAuth';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import MainCard from 'components/MainCard';
import Snackbar from 'utils/Snackbar';
import { getAllDivision } from 'services/division.service';
// --- Types & Styled Components remain the same ---
interface Period {
  periodNumber: number;
  subject: string;
  teacher?: string;
  type?: 'Lecture' | 'Lab' | 'Break' | 'Assembly';
  room?: string;
}
interface DivisionOption {
  _id: string;
  divisionName: string;
}

interface DaySchedule {
  day: string;
  periods: Period[];
}

interface ClassSchedule {
  classId: string;
  className: string;
  schedule: DaySchedule[];
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #e0e0e0',
  padding: '8px',
  textAlign: 'center',
  '&:first-of-type': {
    paddingLeft: '8px !important'
  },
  '&.header': {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    position: 'sticky',
    top: 0
    // zIndex: 10
  },
  '&.row-header': {
    backgroundColor: '#fafafa',
    fontWeight: 'bold',
    position: 'sticky',
    left: 0,
    // zIndex: 5,
    width: '120px',
    minWidth: '120px'
  },
  '&.period-cell': {
    minWidth: '150px'
  }
}));

const SubjectText = styled(Typography)({
  fontWeight: 600,
  fontSize: '0.95rem'
});
const TeacherText = styled(Typography)({
  fontSize: '0.8rem',
  color: '#555',
  marginTop: '4px'
});

const getUniqueDays = (data: ClassSchedule[]): string[] => {
  const days = new Set<string>();
  data.forEach((cls) => {
    cls.schedule.forEach((daySch) => {
      if (daySch?.day) days.add(daySch.day);
    });
  });
  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  return Array.from(days).sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
};

const getMaxPeriodsForDay = (data: ClassSchedule[], day: string): number => {
  let max = 0;
  data.forEach((cls) => {
    const daySch = cls.schedule.find((d) => d.day === day);
    if (daySch && Array.isArray(daySch.periods) && daySch.periods.length > max) {
      max = daySch.periods.length;
    }
  });
  return max;
};

const MenuProps = {
  PaperProps: { style: { maxHeight: 48 * 5 + 8, width: 300 } }
};

// --- Main Component ---
const TimeTableViewer: React.FC = () => {
  const [data, setData] = useState<ClassSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { user }: any = useAuth();
  const currentYear = new Date().getFullYear();
  const [divisions, setDivisions] = useState<any[]>([]);
  const [divisionLoading, setDivisionLoading] = useState(false);

  const academicYears = useMemo(() => {
    const years = [];
    for (let y = currentYear - 5; y <= currentYear + 2; y++) {
      years.push(`${y}-${y + 1}`);
    }
    return years;
  }, [currentYear]);

  const [selectedAcademicYear, setSelectedAcademicYear] = useState<string>(`${currentYear}-${currentYear + 1}`);
  const [selectedDivision, setSelectedDivision] = useState<string[]>([]);

  const orgId = user?.organizationId?._id;

  // --- Fetch & Generate Functions (unchanged) ---
  const fetchTimetable = async (academicYear: string) => {
    if (!orgId) {
      // Snackbar("Organization not found.", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await getFullTimeTable({ orgId, academicYear });

      const resData = response.data;
      let rawTimetables: any[] = [];

      if (resData?.timetables && Array.isArray(resData.timetables)) {
        rawTimetables = resData.timetables;
      } else if (Array.isArray(resData)) {
        rawTimetables = resData;
      }

      if (rawTimetables.length === 0) {
        setData([]);
        Snackbar(`No timetable data found for ${academicYear}. Please generate it first.`, 'warning');
        return;
      }

      const mappedData: ClassSchedule[] = rawTimetables.map((item: any) => ({
        classId: item.divisionId || item.classId,
        className: item.divisionName || item.className,
        schedule: (item.schedule || []).map((day: any) => ({
          day: day.day,
          periods: (day.periods || []).map((p: any) => ({
            periodNumber: p.periodNumber,
            subject: p.subject,
            teacher: p.teacher || undefined,
            type: p.type || undefined
          }))
        }))
      }));

      setData(mappedData);
    } catch (err: any) {
      console.error('Error fetching timetable:', err);
      Snackbar(err?.response?.data?.message || err?.message || 'Failed to load timetable.', 'error');
    } finally {
      setLoading(false);
    }
  };
  const fetchDivisions = async () => {
    if (!orgId) return;

    try {
      setDivisionLoading(true);
      const res = await getAllDivision({ orgId });
      const divisionArray = res?.data?.data?.divisions || [];

      setDivisions(divisionArray);
    } catch (err: any) {
      console.error('Failed to fetch divisions', err);
      Snackbar(err?.response?.data?.message || 'Failed to load divisions', 'error');
    } finally {
      setDivisionLoading(false);
    }
  };

  const handleGenerateTimetable = async () => {
    if (!orgId) {
      Snackbar('Organization not found.', 'error');
      return;
    }

    try {
      setGenerating(true);
      await generateTimetable({ orgId, academicYear: selectedAcademicYear });
      Snackbar(`Timetable generated successfully for ${selectedAcademicYear}!`, 'success');
      await fetchTimetable(selectedAcademicYear);
    } catch (err: any) {
      console.error('Generation error:', err);
      Snackbar(err?.response?.data?.errors?.[0] || err?.message || 'Failed to generate timetable.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  React.useEffect(() => {
    if (orgId && selectedAcademicYear) {
      fetchTimetable(selectedAcademicYear);
    }
  }, [selectedAcademicYear, orgId]);

  const classes = useMemo(
    () => data.filter((c) => (selectedDivision.length > 0 ? selectedDivision.includes(c.classId) : true)),
    [data, selectedDivision]
  );

  const days = useMemo(() => getUniqueDays(classes), [classes]);

  const hasTimetable = classes.length > 0 && days.length > 0;
  React.useEffect(() => {
    if (orgId) {
      fetchDivisions();
    }
  }, [orgId]);

  return (
    <>
      <MainCard
        title={
          <Box display="flex" alignItems="center">
            <BackToggle show />
            <Box ml={2}>
              <Typography variant="h5">Timetable Viewer</Typography>
            </Box>
          </Box>
        }
        content={false}
      >
        <Box sx={{ p: 3 }}>
          {/* Controls: All in one responsive row */}
          <Grid2 container spacing={2} alignItems="center" sx={{ mb: 4 }}>
            {/* Division Filter */}
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="select-division-label" shrink>
                  Filter By Division
                </InputLabel>
                <Select
                  labelId="select-division-label"
                  multiple
                  value={selectedDivision}
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    if (value.includes('ALL')) {
                      setSelectedDivision([]);
                    } else {
                      setSelectedDivision(value);
                    }
                  }}
                  input={<OutlinedInput label="Division" />}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.length === 0) return 'All';

                    return divisions
                      .filter((d) => selected.includes(d._id))
                      .map((d) => d.divisionName)
                      .join(', ');
                  }}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="ALL">
                    <Checkbox checked={selectedDivision.length === 0} />
                    <ListItemText primary="All" />
                  </MenuItem>

                  {divisionLoading && (
                    <MenuItem disabled>
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                      Loading...
                    </MenuItem>
                  )}

                  {divisions.map((d) => (
                    <MenuItem key={d._id} value={d._id}>
                      <Checkbox checked={selectedDivision.includes(d._id)} />
                      <ListItemText primary={d.divisionName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            {/* Academic Year */}
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={selectedAcademicYear}
                  label="Academic Year"
                  onChange={(e) => setSelectedAcademicYear(e.target.value as string)}
                >
                  {academicYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            {/* Generate Button - Right aligned */}
            <Grid2 size={{ xs: 12, sm: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleGenerateTimetable}
                disabled={generating || loading}
                startIcon={generating ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ fontWeight: 600 }}
              >
                {generating ? 'Generating...' : 'Generate Timetable'}
              </Button>
            </Grid2>
          </Grid2>
          {/* Current Selection Chips */}
          <Box sx={{ mb: 4, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Year: ${selectedAcademicYear}`} color="primary" variant="outlined" />
            {selectedDivision.length > 0 && (
              <Chip
                label={`${selectedDivision.length} Division${selectedDivision.length > 1 ? 's' : ''} Selected`}
                color="info"
                variant="outlined"
              />
            )}
          </Box>
          {/* Loading / Empty States */}
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading timetable for {selectedAcademicYear}...</Typography>
            </Box>
          )}
          {!hasTimetable && !loading && (
            <Box textAlign="center" my={10}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No timetable available for {selectedAcademicYear}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Click <strong>"Generate Timetable"</strong> to create one.
              </Typography>
            </Box>
          )}
          {/* Timetable Table */}
          {hasTimetable && (
            <Box
              sx={{
                width: '100%',
                overflow: 'auto',
                borderRadius: 2,
                boxShadow: 2
              }}
            >
              <TableContainer component={Paper} sx={{ maxHeight: '80vh' }}>
                <Table stickyHeader aria-label="timetable grid">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell
                        className="header row-header"
                        sx={{
                          zIndex: 20,
                          padding: '0 !important',
                          '&:first-of-type': { padding: '0 !important' }
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            height: '100%',
                            minHeight: '70px',
                            background:
                              'linear-gradient(to top right, transparent calc(50% - 1px), #c0c0c0 calc(50% - 1px), #c0c0c0 calc(50% + 1px), transparent calc(50% + 1px))'
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              position: 'absolute',
                              bottom: 10,
                              left: 10,
                              fontWeight: 'bold'
                            }}
                          >
                            Day
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              fontWeight: 'bold'
                            }}
                          >
                            Division
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      {classes.map((cls) => (
                        <StyledTableCell key={cls.classId} className="header">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {cls.className}
                          </Typography>
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {days.map((day) => {
                      const maxPeriods = getMaxPeriodsForDay(data, day);
                      const periodNumbers = Array.from({ length: maxPeriods }, (_, i) => i + 1);

                      return (
                        <React.Fragment key={day}>
                          {periodNumbers.map((periodNum) => (
                            <TableRow key={`${day}-${periodNum}`} hover>
                              <StyledTableCell className="row-header">
                                {day} <br />
                                <Typography variant="caption" color="text.secondary">
                                  Period {periodNum}
                                </Typography>
                              </StyledTableCell>
                              {classes.map((cls) => {
                                const daySch = cls.schedule.find((d) => d.day === day);
                                const period = daySch?.periods.find((p) => p.periodNumber === periodNum);

                                return (
                                  <StyledTableCell key={`${cls.classId}-${day}-${periodNum}`} className="period-cell">
                                    {period ? (
                                      <Box
                                        sx={{
                                          bgcolor:
                                            period.type === 'Break' ? '#fff3e0' : period.type === 'Assembly' ? '#e8f5e9' : 'transparent',
                                          p: 1.5,
                                          borderRadius: 1.5,
                                          minHeight: '60px',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          justifyContent: 'center'
                                        }}
                                      >
                                        <SubjectText>{period.subject}</SubjectText>
                                        {period.teacher && <TeacherText>{period.teacher}</TeacherText>}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.disabled">
                                        —
                                      </Typography>
                                    )}
                                  </StyledTableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell colSpan={classes.length + 1} sx={{ bgcolor: '#f5f5f5', height: '16px', p: 0 }} />
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </MainCard>
    </>
  );
};

export default TimeTableViewer;
